import React, { useEffect, useState, useContext } from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Typography, Box, LinearProgress
} from "@material-ui/core";
import TranscriptsService from "../../../../../services/api/transcripts.service";
import { store } from "../../../../../context/StateProvider";
import APIResponse from "../../../../templates/components/APIResponse";
import DimLoader from "../../../../templates/components/DimLoader";

/**
 * Props:
 *  - open: boolean
 *  - onClose: () => void
 *  - patientId: string
 *  - onJobStarted?: (jobName: string) => void
 */
export default function UploadAudioDialog({ open, onClose, patientId, onJobStarted }) {
  const [file, setFile] = useState(null);           // File
  const [url, setUrl] = useState(null);             // object URL for preview
  const [duration, setDuration] = useState(null);   // seconds
  const [uploading, setUploading] = useState(false);
  const [apiResponse, setApiResponse] = useState();
  const state = useContext(store).state;
  const [locationId, setLocationId] = useState(state.practiceLocationId || null);

  const onSuccess = (message) => {
    if (onJobStarted) {
      // Extract jobName from the last successful operation if available
      const jobName = message || "transcription-job";
      onJobStarted(jobName);
    }
    if (onClose) {
      onClose();
    }
  };

  useEffect(() => {
    if (!open) {
      if (url) URL.revokeObjectURL(url);
      setUrl(null);
      setFile(null);
      setDuration(null);
      setUploading(false);
      setApiResponse(null);
    }
  }, [open, url]);

  const onPick = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    // NOTE: Your backend signs with Content-Type=audio/mpeg today.
    // To avoid signature mismatch, restrict to MP3 for now.
    if (f.type && f.type !== "audio/mpeg" && f.type !== "audio/mp3") {
      alert("Please select an MP3 file.");
      return;
    }
    if (url) URL.revokeObjectURL(url);
    setFile(f);
    setUrl(URL.createObjectURL(f));
    setDuration(null);
  };

  const clearFile = () => {
    if (url) URL.revokeObjectURL(url);
    setUrl(null);
    setFile(null);
    setDuration(null);
  };

  const uploadAndStart = async () => {
    if (!file) {
      alert("Please select a file first.");
      return;
    }
    setUploading(true);
    setApiResponse(null);

    // Step 1: Get presigned URL
    TranscriptsService.getUploadUrl(patientId, locationId)
      .then(async (urlResponse) => {
        const { uploadURL, jobName } = urlResponse;
        
        // Step 2: Upload to S3
        const contentType = file.type || "audio/mpeg";
        return TranscriptsService.uploadAudioToS3(uploadURL, file, contentType)
          .then(() => {
            // Step 3: Start transcription job
            return TranscriptsService.startMedicalScribeJob(patientId, jobName)
              .then((response) => {
                // All steps completed successfully
                setApiResponse({
                  ...response,
                  message: response?.message || "Upload complete. Transcription job started.",
                  data: { jobName }
                });
              });
          });
      })
      .catch(error => {
        console.error("Upload/Transcription error:", error);
        setApiResponse(error);
      })
      .finally(() => {
        setUploading(false);
      });
  };

  return (
    <>
      {uploading && <DimLoader loadMessage="Uploading & Processing..." />}
      
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle>Upload Existing Audio</DialogTitle>
        <DialogContent>
          <Box mb={2}>
            <input
              type="file"
              accept="audio/mpeg,.mp3"
              onChange={onPick}
              disabled={uploading}
            />
            <Typography variant="caption" color="textSecondary" display="block">
              Accepted: MP3 files only.
            </Typography>
          </Box>

          {file && (
            <Box
              style={{ border: "1px solid #e0e0e0", borderRadius: 6, padding: 12 }}
            >
              <Typography variant="subtitle2" gutterBottom>
                Preview
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {file.name} · {(file.size / 1024 / 1024).toFixed(2)} MB
                {duration != null ? ` · ${Math.round(duration)}s` : ""}
              </Typography>
              <audio
                src={url}
                controls
                style={{ width: "100%", marginTop: 8 }}
                onLoadedMetadata={(e) => {
                  try { setDuration(e.currentTarget.duration); } catch {}
                }}
              />
              <Box mt={2} display="flex" gridGap={8}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={uploadAndStart}
                  disabled={uploading}
                >
                  {uploading ? "Uploading…" : "Upload & Start Transcript"}
                </Button>
                <Button variant="text" onClick={clearFile} disabled={uploading}>
                  Clear
                </Button>
              </Box>
            </Box>
          )}

          {uploading && (
            <Box mt={2}>
              <LinearProgress />
            </Box>
          )}

          {!file && (
            <Box mt={2} color="#666">
              <Typography variant="subtitle2">Tips</Typography>
              <ul style={{ marginTop: 4 }}>
                <li>Select an MP3 recording of the visit.</li>
              </ul>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <button className='btn btn-primary me-2 ' 
              onClick={onClose} disabled={uploading}>
              Close
          </button>
        </DialogActions>
      </Dialog>
      
      <APIResponse apiResponse={apiResponse} onSuccess={onSuccess} toastOnSuccess={true} />
    </>
  );
}
